import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BASE_URL1 } from "../utils/apiURL";
import { STATUS } from "../utils/status";

const initialState = {
  user: null, // État pour stocker l'utilisateur connecté
  token: null, // État pour stocker le token
  authStatus: STATUS.IDLE,
  error: null, // État pour stocker les erreurs
};

// Async thunk pour la connexion
// export const loginUser = createAsyncThunk(
//   "auth/login",
//   async ({ username, password }) => {
//     const response = await fetch(`${BASE_URL1}Clientlogin`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ username, password }),
//     });

//     if (!response.ok) {
//       const errorData = await response.json();
//       if (response.status === 403) {  // Vérifie si le compte est inactif
//         throw new Error("Your account is inactive");
//       }
//       throw new Error(errorData.message || "Connection Failed");
//     }

//     const data = await response.json();
//     return data; // Cela doit contenir les informations sur l'utilisateur et le token
//   }
// );

export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ username, password }) => {
    const response = await fetch(`${BASE_URL1}Clientlogin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 403) {
        // Vérifie si le compte est inactif
        throw new Error("Your account is inactive");
      }
      throw new Error(errorData.message || "Connection Failed");
    }

    const data = await response.json();

    // Vérifiez si un OTP a été envoyé dans la réponse
    if (
      data.success &&
      data.message === "Otp code send successful. verify your email"
    ) {
      // Ici, l'OTP est envoyé, et vous pouvez l'inclure dans la réponse
      return { ...data, otpSent: true }; // Ajoutez un champ pour signaler que l'OTP a été envoyé
    }

    return data; // Retourner les informations de l'utilisateur comme avant
  }
);

// Async thunk pour la vérification de l'OTP
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (Otpcode, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_URL1}otp/verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ Otpcode }),
      });

      const data = await response.json(); // Récupérer la réponse JSON du backend

      // Vérifier si la réponse indique une erreur (si success est false)
      if (!response.ok || !data.success) {
        // Si l'OTP est invalide, l'expiration ou un autre problème, on renvoie un message d'erreur
        return rejectWithValue(data.message || "OTP verification failed");
      }

      // Si tout se passe bien, retour de l'information de succès
      return { message: data.message, token: data.token, user: data.user };
    } catch (error) {
      // Si une erreur survient (erreur réseau, etc.), nous renvoyons le message d'erreur
      return rejectWithValue(error.message || "An unknown error occurred");
    }
  }
);

// Async thunk pour récupérer les informations de l'utilisateur connecté
export const fetchUserData = createAsyncThunk(
  "auth/fetchUserData",
  async (token) => {
    const response = await fetch(`${BASE_URL1}user/info`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`, // Utilisation du token pour l'authentification
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      throw new Error("Error retrieving user data");
    }

    const data = await response.json();
    return data.user; // Retourne l'utilisateur
  }
);

export const updateUserProfile = createAsyncThunk(
  "auth/updateProfile",
  async ({ profileData }, { rejectWithValue }) => {
    const token = localStorage.getItem("token");

    if (!token) {
      return rejectWithValue({ message: "User is not authenticated." });
    }

    try {
      const response = await fetch(`${BASE_URL1}update/Profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          return rejectWithValue({
            message: "Token expired. Please log in again.",
          });
        }
        return rejectWithValue({ message: data.message || "Update failed." });
      }

      return data;
    } catch (error) {
      if (error instanceof TypeError) {
        return rejectWithValue({
          message: "Network error. Please check your connection.",
        });
      }
      return rejectWithValue({ message: "An unexpected error occurred." });
    }
  }
);

// Async thunk pour mettre à jour le mot de passe de l'utilisateur
export const updateUserPassword = createAsyncThunk(
  "auth/updatePassword",
  async (
    { current_password, password, password_confirmation },
    { rejectWithValue }
  ) => {
    const token = localStorage.getItem("token");

    if (!token) {
      return rejectWithValue({ message: "User is not authenticated." });
    }

    try {
      const response = await fetch(`${BASE_URL1}update/User/Password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          current_password,
          password,
          password_confirmation,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue({
          message: data.message || "Password update failed.",
        });
      }
      return data; // Retourne la réponse du backend
    } catch (error) {
      return rejectWithValue({ message: "Network error. Please try again." });
    }
  }
);

// Async thunk pour la déconnexion
export const logoutUser = createAsyncThunk("auth/logout", async () => {
  return null; // Pas besoin de retourner quoi que ce soit pour la déconnexion
});

// Création du slice d'authentification
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.error = null; // Réinitialiser l'erreur
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyOtp.pending, (state) => {
        state.authStatus = STATUS.LOADING;
        state.error = null; // Réinitialiser les erreurs avant de tenter de vérifier l'OTP
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.token = action.payload.token; // Stocke le token ici
        state.user = action.payload.user; // Met à jour les données de l'utilisateur
        localStorage.setItem("token", action.payload.token); // Sauvegarde le token dans le localStorage
        state.authStatus = STATUS.SUCCEEDED;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.authStatus = STATUS.FAILED;
        state.error = action.payload || action.error.message; // Stocker le message d'erreur
      })
      .addCase(loginUser.pending, (state) => {
        state.authStatus = STATUS.LOADING;
        state.error = null; // Réinitialiser les erreurs avant de tenter de se connecter
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.authStatus = STATUS.SUCCEEDED;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.authStatus = STATUS.FAILED;
        state.error = action.error.message; // Stocker le message d'erreur
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.user = action.payload; // Met à jour l'utilisateur avec les données récupérées
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null; // Réinitialiser l'utilisateur à la déconnexion
        state.token = null; // Réinitialiser le token
        state.authStatus = STATUS.IDLE; // Réinitialiser le statut d'authentification
        localStorage.removeItem("token"); // Supprimer le token du local storage
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.authStatus = STATUS.LOADING;
        state.error = null; // Réinitialiser les erreurs avant de tenter de mettre à jour
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.user = action.payload; // Met à jour l'utilisateur dans le state
        state.authStatus = STATUS.SUCCEEDED;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.authStatus = STATUS.FAILED;
        state.error = action.error.message; // Stocker le message d'erreur
      })
      .addCase(updateUserPassword.pending, (state) => {
        state.authStatus = STATUS.LOADING;
        state.error = null; // Réinitialiser les erreurs avant de tenter de mettre à jour
      })
      .addCase(updateUserPassword.fulfilled, (state, action) => {
        state.authStatus = STATUS.SUCCEEDED;
        state.error = null; // Réinitialiser l'erreur après une mise à jour réussie
      })
      .addCase(updateUserPassword.rejected, (state, action) => {
        state.authStatus = STATUS.FAILED;
        state.error = action.error.message; // Stocker le message d'erreur
      });
  },
});

// Selectors
export const selectUser = (state) => state.auth.user;
export const selectAuthStatus = (state) => state.auth.authStatus;
export const selectAuthError = (state) => state.auth.error;

// Actions exportées
export const { clearAuthError } = authSlice.actions;

export default authSlice.reducer;
